import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RulesRegulationsService } from '../../services/rulesRegulationsService';

export const fetchCategories = createAsyncThunk(
  'rulesRegulations/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.getCategories();
      // Handle both cases: response.data, response.Data, or if response is already the array
      return response.data || response.Data || (Array.isArray(response) ? response : []);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  'rulesRegulations/createCategory',
  async ({ data, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.createCategory(data, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'rulesRegulations/updateCategory',
  async ({ id, data, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.updateCategory(id, data, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAdminRules = createAsyncThunk(
  'rulesRegulations/fetchAdminRules',
  async (params, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.getAdminRules(params);
      return response.data || response.Data || (Array.isArray(response) ? response : []);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createRule = createAsyncThunk(
  'rulesRegulations/createRule',
  async ({ formData, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.createRule(formData, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateRule = createAsyncThunk(
  'rulesRegulations/updateRule',
  async ({ id, formData, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.updateRule(id, formData, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const publishRule = createAsyncThunk(
  'rulesRegulations/publishRule',
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.publishRule(id, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const archiveRule = createAsyncThunk(
  'rulesRegulations/archiveRule',
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.archiveRule(id, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchMyRules = createAsyncThunk(
  'rulesRegulations/fetchMyRules',
  async ({ userId, siteId, wingId, floorId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.getMyRules(userId, siteId, wingId, floorId);
      const data = response.data || response.Data || response;
      return data || { siteRules: [], wingRules: [], floorRules: [] };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const acknowledgeRule = createAsyncThunk(
  'rulesRegulations/acknowledgeRule',
  async ({ id, ruleVersionId, deviceId, userId }, { rejectWithValue }) => {
    try {
      const response = await RulesRegulationsService.acknowledgeRule(id, ruleVersionId, deviceId, userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  categories: {
    data: [],
    loading: false,
    error: null,
  },
  adminRules: {
    data: [],
    loading: false,
    error: null,
  },
  myRules: {
    siteRules: [],
    wingRules: [],
    floorRules: [],
    loading: false,
    error: null,
  },
};

const rulesRegulationsSlice = createSlice({
  name: 'rulesRegulations',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.categories.error = null;
      state.adminRules.error = null;
      state.myRules.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Categories
      .addCase(fetchCategories.pending, (state) => {
        state.categories.loading = true;
        state.categories.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories.loading = false;
        state.categories.data = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = action.payload;
      })
      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.categories.loading = true;
      })
      .addCase(createCategory.fulfilled, (state) => {
        state.categories.loading = false;
        // We will refetch categories after this succeeds
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = action.payload;
      })
      // Update Category
      .addCase(updateCategory.pending, (state) => {
        state.categories.loading = true;
      })
      .addCase(updateCategory.fulfilled, (state) => {
        state.categories.loading = false;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = action.payload;
      })
      // Admin Rules
      .addCase(fetchAdminRules.pending, (state) => {
        state.adminRules.loading = true;
        state.adminRules.error = null;
      })
      .addCase(fetchAdminRules.fulfilled, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.data = action.payload;
      })
      .addCase(fetchAdminRules.rejected, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.error = action.payload;
      })
      // Create Rule
      .addCase(createRule.pending, (state) => {
        state.adminRules.loading = true;
      })
      .addCase(createRule.fulfilled, (state) => {
        state.adminRules.loading = false;
      })
      .addCase(createRule.rejected, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.error = action.payload;
      })
      // Update Rule
      .addCase(updateRule.pending, (state) => {
        state.adminRules.loading = true;
      })
      .addCase(updateRule.fulfilled, (state) => {
        state.adminRules.loading = false;
      })
      .addCase(updateRule.rejected, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.error = action.payload;
      })
      // Publish Rule
      .addCase(publishRule.pending, (state) => {
        state.adminRules.loading = true;
      })
      .addCase(publishRule.fulfilled, (state) => {
        state.adminRules.loading = false;
      })
      .addCase(publishRule.rejected, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.error = action.payload;
      })
      // Archive Rule
      .addCase(archiveRule.pending, (state) => {
        state.adminRules.loading = true;
      })
      .addCase(archiveRule.fulfilled, (state) => {
        state.adminRules.loading = false;
      })
      .addCase(archiveRule.rejected, (state, action) => {
        state.adminRules.loading = false;
        state.adminRules.error = action.payload;
      })
      // My Rules
      .addCase(fetchMyRules.pending, (state) => {
        state.myRules.loading = true;
        state.myRules.error = null;
      })
      .addCase(fetchMyRules.fulfilled, (state, action) => {
        state.myRules.loading = false;
        state.myRules.siteRules = action.payload.siteRules || action.payload.SiteRules || [];
        state.myRules.wingRules = action.payload.wingRules || action.payload.WingRules || [];
        state.myRules.floorRules = action.payload.floorRules || action.payload.FloorRules || [];
      })
      .addCase(fetchMyRules.rejected, (state, action) => {
        state.myRules.loading = false;
        state.myRules.error = action.payload;
      })
      // Acknowledge Rule
      .addCase(acknowledgeRule.pending, (state) => {
        // optionally handle loading
      })
      .addCase(acknowledgeRule.fulfilled, (state, action) => {
        // optionally handle success silently (UI refetches)
      })
      .addCase(acknowledgeRule.rejected, (state, action) => {
        // optionally handle error
      });
  }
});

export const { clearErrors } = rulesRegulationsSlice.actions;
export default rulesRegulationsSlice.reducer;
